import React from 'react';
import { useAuthContext } from '../hooks/AuthContext';

interface ProtectedRouteProps {
  page: string;
  children: React.ReactNode;
}

/**
 * Wraps a route/page and only renders children if the user has view permission for that page.
 * Shows a loading state while permissions are being fetched, and an access denied message if blocked.
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ page, children }) => {
  const { canView, loading, authUser } = useAuthContext();

  if (loading) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center' }}>
        <div className="loading">Checking access...</div>
      </div>
    );
  }

  if (!authUser) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center' }}>
        <h2>⚠️ Authentication Error</h2>
        <p style={{ color: '#666' }}>Unable to verify your access. Please reload the page or sign out and back in.</p>
      </div>
    );
  }

  if (!canView(page)) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', maxWidth: '500px', margin: '4rem auto' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔒</div>
        <h2 style={{ margin: '0 0 0.5rem' }}>Access Denied</h2>
        <p style={{ color: '#666', margin: 0 }}>
          You don't have permission to access this page. Contact your administrator if you believe this is an error.
        </p>
        <p style={{ color: '#999', fontSize: '0.85rem', marginTop: '1rem' }}>
          Your role: <strong style={{ textTransform: 'capitalize' }}>{authUser.access_role}</strong>
        </p>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
